// StudioNeedsYou — the action panel's Needs you (pen "Studio · Action
// panel", Needs you chosen): the whole queue of what stands between the
// author and export, one numbered list, each item with its one action.
// Projected from the project's export check (`Packaging.Issues`, PS-54):
// what to check first (suggestions and AI drafts: Go through), then what
// to fill (planned, empty or removed parts: Open; uncovered learning goals
// have no place to open yet, so no action); a kind this build does not know
// joins the checks. Last, Before export: what the export target's format
// leaves out or moves, and its planned documents with "Generate it". Then
// the assistant's proposals for how the course lands in its format (the
// same cards as the How it lands panel: Show me, Move it). An item's action
// opens its thing in the middle. Pure.

export type { BeforeExport };
export { needsYouCount, needsYouQueue, StudioNeedsYou };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type { MoveTextProposal } from "../../app/ports/landing.js";
import type { ProjectIssue, ProjectIssues } from "../../app/ports/project.js";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { Icon } from "../Icon/Icon.js";
import { BeforeExportRows } from "../StudioDesk/DeskBriefs.js";
import type { DeskNote, ReadinessItem } from "../StudioDesk/deskFacts.js";
import { issueTarget } from "../StudioExport/exportCheck.js";
import { issueKey, useIssueText } from "../StudioExport/StudioExport.js";
import { LandingProposalCard } from "../StudioLanding/HowItLands.js";

// What stands before export of the course or module Export sends out
// (DeskBriefs.beforeExportItems), with what "Generate it" writes.
type BeforeExport = {
    // The course or module the check is for.
    readonly where: string;
    readonly items: readonly ReadinessItem[];
    readonly planned: readonly string[];
    readonly note: DeskNote | undefined;
    readonly onGenerateAll: (ids: readonly string[]) => void;
};

// Checks come before fills.
type Step = "check" | "fill";

const stepOf = (issue: ProjectIssue): Step => {
    switch (issue.type) {
        case "issue_draft_unreviewed":
        case "issue_proposals_open":
        case "unknown":
            return "check";
        case "issue_parts_planned":
        case "issue_parts_empty":
        case "issue_parts_removed":
        case "issue_objectives_not_covered":
            return "fill";
        default:
            return exhaustSwitchCase(issue);
    }
};

// The queue's issues in order: checks, then fills. No answer yet: none.
const needsYouQueue = (
    issues: ProjectIssues | undefined,
): readonly ProjectIssue[] => {
    if (issues?.type !== "project_has_issues") return [];
    const all = issues.issues;
    return [
        ...all.filter((i) => stepOf(i) === "check"),
        ...all.filter((i) => stepOf(i) === "fill"),
    ];
};

// How many things wait: every issue the check found (also those past the
// list's end) and every item before export. 0: nothing, or no answer yet.
const needsYouCount = (
    issues: ProjectIssues | undefined,
    beforeExport: BeforeExport | undefined,
): number =>
    (issues?.type === "project_has_issues" ? issues.total : 0) +
    (beforeExport?.items.length ?? 0);

// An issue's one action: what opening its thing lets the author do.
const useActionLabel = (issue: ProjectIssue): string | undefined => {
    const { t } = useLingui();
    switch (issue.type) {
        case "issue_draft_unreviewed":
        case "issue_proposals_open":
            return t`Go through`;
        case "issue_parts_planned":
        case "issue_parts_empty":
        case "issue_parts_removed":
            return t`Open`;
        case "issue_objectives_not_covered":
        case "unknown":
            return undefined;
        default:
            return exhaustSwitchCase(issue);
    }
};

const IssueItem = ({
    issue,
    onOpen,
}: {
    readonly issue: ProjectIssue;
    readonly onOpen: (id: string) => void;
}): ReactNode => {
    const text = useIssueText(issue);
    const action = useActionLabel(issue);
    const target = issueTarget(issue);
    const body = (
        <>
            <span
                className="bilda-studio-export__icon"
                data-icon={text.icon}
                aria-hidden="true"
            >
                <Icon name={text.icon} size="xs" />
            </span>
            <span className="bilda-studio-export__text">
                <span className="bilda-studio-export__what">{text.what}</span>
                {text.where === "" ? null : (
                    <span className="bilda-studio-export__where">
                        {text.where}
                    </span>
                )}
            </span>
        </>
    );
    return target === undefined || action === undefined ? (
        <div className="bilda-studio-export__row">{body}</div>
    ) : (
        <button
            type="button"
            className="bilda-studio-export__row"
            onClick={() => onOpen(target)}
        >
            {body}
            <span className="bilda-studio-needs__action">{action}</span>
        </button>
    );
};

const StudioNeedsYou = ({
    issues,
    beforeExport,
    proposals,
    moving,
    onShowMe,
    onMoveIt,
    online,
    sourced,
    onOpen,
    onAddSource,
}: {
    // The project's export check; undefined: no answer yet.
    readonly issues: ProjectIssues | undefined;
    // Absent: nothing to say before export (or no answer yet).
    readonly beforeExport: BeforeExport | undefined;
    // How it lands: text to move (none: nothing proposed, or no format).
    readonly proposals: readonly MoveTextProposal[];
    readonly moving: string | undefined;
    readonly onShowMe: (p: MoveTextProposal) => void;
    readonly onMoveIt: (p: MoveTextProposal) => void;
    readonly online: boolean;
    // The project has a source: planned parts fill from it, else Add a source.
    readonly sourced: boolean;
    readonly onOpen: (id: string) => void;
    readonly onAddSource: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const queue = needsYouQueue(issues);
    const before = beforeExport?.items ?? [];
    if (issues === undefined)
        return <p className="bilda-studio-needs__waiting">{t`Checking …`}</p>;
    if (queue.length === 0 && before.length === 0 && proposals.length === 0)
        return <EmptyState title={t`Nothing needs you right now`} />;
    const more =
        issues.type === "project_has_issues"
            ? issues.total - issues.issues.length
            : 0;
    return (
        <div className="bilda-studio-needs">
            <ol className="bilda-studio-needs__rows">
                {queue.map((issue, i) => (
                    <li key={issueKey(issue, i)}>
                        <IssueItem issue={issue} onOpen={onOpen} />
                    </li>
                ))}
                {beforeExport === undefined ? null : (
                    <BeforeExportRows
                        items={before}
                        where={beforeExport.where}
                        planned={beforeExport.planned}
                        note={beforeExport.note}
                        online={online}
                        sourced={sourced}
                        onGenerateAll={beforeExport.onGenerateAll}
                        onAddSource={onAddSource}
                    />
                )}
            </ol>
            {more > 0 ? (
                <p className="bilda-studio-needs__more">{t`and ${more} more`}</p>
            ) : null}
            {proposals.map((p) => (
                <LandingProposalCard
                    key={`${p.container}:${p.part}`}
                    proposal={p}
                    online={online}
                    moving={moving === p.part}
                    onShowMe={() => onShowMe(p)}
                    onMoveIt={() => onMoveIt(p)}
                />
            ))}
        </div>
    );
};
